import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
import { categories } from '../data/products';
import { getRegionMeta } from '../utils/region';
export function Contact() {
  const region = getRegionMeta();
  const addressPlaceholder = `123 Main St, ${region.cities[0]}, WA`;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = e.target as HTMLFormElement;
      const materialSelect = form.querySelector('select[class*="appearance-none"]') as HTMLSelectElement;
      // Estimated Yardage is now a free-text input (was previously a
      // restrictive dropdown with a wrong "5 yd minimum"). Read it by name.
      const yardageInput = form.querySelector('input[name="estimatedYardage"]') as HTMLInputElement | null;
      const addressInput = form.querySelector('input[name="deliveryAddress"]') as HTMLInputElement;
      const rc = region.code;
      const brandName = `${rc} Gravel`;
      await sendFormEmail({
        subject: `${rc}Gravel — New Contact / Quote Request`,
        'First Name': formData.firstName,
        'Last Name': formData.lastName,
        email: formData.email,
        _replyto: formData.email,
        _autoresponse: `Hi ${formData.firstName}! Thank you for reaching out to ${brandName}! We've received your message and will get back to you shortly.\n\nIf you need anything sooner, call us at (206) 619-3742.\n\n- The ${brandName} Team`,
        Phone: formData.phone,
        Material: materialSelect?.value || '',
        'Estimated Yardage': yardageInput?.value || '',
        'Delivery Address': addressInput?.value || '',
        Message: formData.message
      });
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setTimeout(() => {
        formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Dark mode keeps the deeper, stepped-down palette; light
  // mode reverts to the original warm-cream tones so the page
  // reads bright and friendly in daylight while still feeling
  // grounded after dark.
  const inputClass = 'w-full bg-[#f6f5f2] dark:bg-[#2d2c2b] border border-stone-300 dark:border-[#3e3d3d] rounded-xl px-4 py-3 text-sm text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent outline-none transition-all';
  return <main className="min-h-[calc(100vh-64px)] bg-[#faf9f7] dark:bg-[#2d2c2b] py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 flex items-center justify-center" data-id="element-2526">
      <div className="max-w-6xl w-full mx-auto" data-id="element-2527">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-10 text-center lg:text-left" data-id="element-2528">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white mb-3 tracking-tight" data-id="element-2529">
            Contact & <span className="text-brand-orange" data-id="element-2530">Quotes</span>
          </h1>
          <p className="text-stone-600 dark:text-stone-300 text-sm md:text-base max-w-2xl mx-auto lg:mx-0 font-sub" data-id="element-2531">
            Need a quote or have a question? Reach out directly or drop your
            project details below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2532">
          {/* Left Column: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} ref={formRef} className="lg:col-span-2 bg-[#fcfbfa] dark:bg-[#3a3939] rounded-2xl p-6 md:p-8 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2533">
            {isSubmitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-2534">
                <div className="w-16 h-16 bg-brand-orange/10 rounded-full flex items-center justify-center mb-4" data-id="element-2535">
                  <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-2536" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-2" data-id="element-2537">
                  Request Received
                </h3>
                <p className="text-stone-600 dark:text-stone-300 mb-6 max-w-sm text-sm" data-id="element-2538">
                  Thanks for reaching out. We'll get back to you shortly with
                  pricing and availability.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="px-6 py-2.5 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-none font-bold text-sm transition-all" data-id="element-2539">
                  Send Another Request
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2540">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2541">
                  <div data-id="element-2542">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2543">
                      First Name *
                    </label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} data-id="element-2544" />
                  </div>
                  <div data-id="element-2545">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2546">
                      Last Name *
                    </label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} data-id="element-2547" />
                  </div>
                </div>

                <div data-id="element-2548">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2549">
                    Email Address *
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-2550" />
                </div>

                <div data-id="element-2551">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2552">
                    Phone Number
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-2553" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2554">
                  <div data-id="element-2555">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2556">
                      Material Needed
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-2557">
                      <option value="" data-id="element-2558">Select a material...</option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} data-id="element-2559">
                          {cat.products.map(p => <option key={p.slug} value={p.name} data-id="element-2560">
                              {p.name} — ${p.pricePerYard}/yd
                            </option>)}
                        </optgroup>)}
                      <option value="Not Sure Yet" data-id="element-2561">Not Sure Yet</option>
                    </select>
                  </div>
                  <div data-id="element-2562">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2563">
                      Estimated Yardage
                    </label>
                    <input type="text" name="estimatedYardage" className={inputClass} placeholder='e.g. "12 yards", "3 truckloads", "not sure"' data-id="element-2564" />
                  </div>
                </div>

                <div data-id="element-2565">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2566">
                    Delivery Address *
                  </label>
                  <input type="text" name="deliveryAddress" required className={inputClass} placeholder={addressPlaceholder} data-id="element-2567" />
                </div>

                <div data-id="element-2568">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2569">
                    Message *
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className={inputClass + ' resize-none'} data-id="element-2570"></textarea>
                </div>

                <div className="pt-2" data-id="element-2571">
                  <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-brand-button hover:bg-brand-buttonHover text-white rounded-none font-bold text-sm transition-all group disabled:opacity-60" data-id="element-2572">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2573" /> : <>
                        Send Quote Request
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2574" />
                      </>}
                  </button>
                </div>
              </form>}
          </motion.div>

          {/* Right Column: Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-6" data-id="element-2575">
            {/* Contact Info Card */}
            <div className="bg-[#fcfbfa] dark:bg-[#3a3939] text-stone-900 dark:text-white rounded-2xl p-6 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2576">
              <h3 className="font-heading text-lg font-bold mb-5" data-id="element-2577">
                Get in Touch
              </h3>
              <div className="space-y-4" data-id="element-2578">
                <a href="tel:206-619-3742" className="flex items-center group" data-id="element-2579">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-2580">
                    <Phone className="w-4 h-4 text-brand-orange" data-id="element-2581" />
                  </div>
                  <div data-id="element-2582">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2583">
                      Call Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2584">
                      (206) 619-3742
                    </p>
                  </div>
                </a>
                <a href="mailto:info@206gravel.com" className="flex items-center group" data-id="element-2585">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-2586">
                    <Mail className="w-4 h-4 text-brand-orange" data-id="element-2587" />
                  </div>
                  <div data-id="element-2588">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2589">
                      Email Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2590">
                      info@206gravel.com
                    </p>
                  </div>
                </a>
                <div className="flex items-center" data-id="element-2591">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3" data-id="element-2592">
                    <MapPin className="w-4 h-4 text-brand-orange" data-id="element-2593" />
                  </div>
                  <div data-id="element-2594">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2595">
                      Location
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2596">
                      Bonney Lake, WA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-[#fcfbfa] dark:bg-[#3a3939] rounded-2xl p-6 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2597">
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-4 flex items-center" data-id="element-2598">
                <Clock className="w-4 h-4 mr-2 text-brand-orange" data-id="element-2599" />
                Hours
              </h3>
              <div className="space-y-3 text-sm" data-id="element-2600">
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#4a4949]" data-id="element-2601">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2602">
                    Mon - Fri
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-2603">
                    6:00 AM – 5:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#4a4949]" data-id="element-2604">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2605">
                    Saturday
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-2606">
                    7:00 AM – 3:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-2607">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2608">
                    Sunday
                  </span>
                  <span className="font-medium text-brand-orange" data-id="element-2609">Closed</span>
                </div>
              </div>
            </div>

            {/* Service Area */}
            <div className="px-1 pt-2" data-id="element-2610">
              <h4 className="font-heading text-sm font-bold text-stone-900 dark:text-white mb-2" data-id="element-2611">
                Service Area
              </h4>
              <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2612">
                Proudly serving King and Pierce counties — Seattle, Bellevue,
                Kirkland, Redmond, Renton, Kent, Auburn, Federal Way, Tacoma,
                Puyallup, Lakewood, Bonney Lake, and beyond — with reliable,
                same-day and next-day delivery. Not sure if you're in our
                delivery zone? Give us a call — we're happy to help.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}